import { RATINGS_TOTAL_STARS } from '@atlassian/amkt-common';
import React from 'react';
import { JsonLd } from 'react-schemaorg';

import type { TileData } from '@atlassian/mpac-app-tile';

export type CollectionsStructuredDataProps = {
  apps: TileData[];
};

/** See https://schema.org/SoftwareApplication */
type SoftwareApplication = {
  ['@type']: 'SoftwareApplication';
  name: string;
  image: string;
  description: string;
  dateCreated?: string;
  applicationCategory: string;
  applicationSuite: string;
  /** See https://schema.org/AggregateRating */
  aggregateRating?: {
    ['@type']: 'AggregateRating';
    bestRating: number;
    ratingValue: number;
    reviewCount: number;
  };
  /** See https://schema.org/Organization */
  creator?: {
    ['@type']: 'Organization';
    name: string;
    url?: string;
  };
};

type SoftwareApplicationSnippet = {
  ['@context']: 'https://schema.org/';
} & SoftwareApplication;

export const APPLICATION_CATEGORY_ADD_ONS = 'Add-ons';

const createAppStructuredData = (app: TileData): SoftwareApplicationSnippet => {
  const appData: SoftwareApplicationSnippet = {
    '@context': 'https://schema.org/',
    '@type': 'SoftwareApplication',
    name: app.name,
    image: app.logo.image,
    description: app.tagLine,
    applicationCategory: APPLICATION_CATEGORY_ADD_ONS,
    applicationSuite: app.deployments.join(', '),
  };

  if (app.releaseDate) {
    appData.dateCreated = new Date(app.releaseDate).toISOString();
  }

  if (app.ratings.numRatings > 0) {
    appData.aggregateRating = {
      '@type': 'AggregateRating',
      bestRating: RATINGS_TOTAL_STARS,
      ratingValue: app.ratings.avgStars,
      reviewCount: app.ratings.numRatings,
    };
  }

  if (app.vendor.name) {
    appData.creator = {
      '@type': 'Organization',
      name: app.vendor.name,
    };
  }

  return appData;
};

export const CollectionsStructuredData = ({ apps }: CollectionsStructuredDataProps) => {
  if (!apps || apps.length === 0) {
    return null;
  }

  const appStructuredData = apps.map(createAppStructuredData);

  return (
    <>
      {appStructuredData.map((app, index) => (
        <JsonLd key={`app-${index}`} item={app} />
      ))}
    </>
  );
};
